import React, { useEffect, useRef, useState } from "react";
import CssTransition from "../../../component/layout/css-transition";
import "../../../css/theme.css";
import * as yup from "yup";
import { useFormik, FieldArray, FormikProvider, Form } from "formik";
import InputField from "../../../component/input-field";
import { useDispatch, useSelector } from "react-redux";
import { sanitizeInput } from "../../../utils";
import Select from "react-select";
import swal from "@sweetalert/with-react";
import CustomeModal from "../../../component/modal-popup/customeModal";
import { createCollaboratorOrCustomer } from "../../../redux/actions/collaboratorOrCustomer";

const schema = yup.object({
  name: yup
    .string()
    .required("Customer name is required.")
    // .matches(/^[a-zA-Z, ]*$/, "Customer name will only contain alphabets")
    .max(50, "Customer name have reached maximum limit of characters allowed."),
  email: yup
    .string()
    .required("Email ID is required.")
    .email("Please enter a valid Email ID."),
  is_trackable: yup.boolean(),
  time_zone_id: yup
    .object()
    .shape({
      label: yup.string(),
      value: yup.string(),
    })
    .required("Time Zone is required.")
    .nullable(),
  additional_email: yup
    .array()
    .of(
      yup
        .string()
        .required("Email ID is required.")
        .email("Please enter a valid Email ID.")
    ),
});

export default function AddCustomer({
  modalOpen,
  setModalOpen,
  update,
  setUpdate,
}) {
  const modalContent = useRef(null);
  const [timezoneOptions, setTimezoneOptions] = useState([]);
  const dispatch = useDispatch();

  const { time_zone_for_onboarding } = useSelector((state) => state.onboarding);

  useEffect(() => {
    let timezone = [];
    time_zone_for_onboarding.forEach((item) => {
      timezone.push({
        value: item.timezone_id,
        label: item.timezone,
      });
    });
    setTimezoneOptions(timezone);
  }, [time_zone_for_onboarding]);

  // Function to call Add Customer API
  const handleAddCollaboratorOrCustomer = (payload) => {
    const request = {
      payload: payload,
      onSuccess: () => {
        setUpdate(update + 1);
        setModalOpen(false);
        swal({
          content: (
            <CustomeModal
              type="success"
              title="Success!"
              content={`Customer is added successfully.`}
            />
          ),
          buttons: false,
          closeOnClickOutside: false,
          closeOnEsc: false,
        })
      },
      onError: (error) => {
        swal({
          content: (
            <CustomeModal
              type="error"
              title="Error!"
              content={
                error.message === "Duplicate entry"
                  ? `Customer already exist with this name. try using another name.`
                  : error.message
              }
            />
          ),
          buttons: false,
          closeOnClickOutside: false,
          closeOnEsc: false,
        });
      },
    };
    dispatch(createCollaboratorOrCustomer(request))
  }

  const addCustomer = (payload) => {
    handleAddCollaboratorOrCustomer(payload);
    formik.resetForm();
  };

  /* -------------- Formik Form Validation ----------------  */
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: "",
      email: "",
      is_trackable: true,
      time_zone_id: null,
      additional_email: [],
    },
    validationSchema: schema,
    onSubmit: (value) => {
      const payload = {
        name: value.name,
        email: value.email,
        email_aliases: value.additional_email,
        time_zone_id: value.time_zone_id.value,
        is_trackable: value.is_trackable,
        type: "customer",
      };
      addCustomer(payload);
    },
  });

  return (
    <>
      <CssTransition
        className="fixed inset-0 bg-slate-900 bg-opacity-30 z-50 transition-opacity"
        show={modalOpen}
        enter="transition ease-out duration-200"
        enterStart="opacity-0"
        enterEnd="opacity-100"
        leave="transition ease-out duration-100"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
        aria-hidden="true"
      />
      <CssTransition
        className="fixed inset-0 z-50 overflow-hidden flex items-center my-4 justify-center px-4 sm:px-6"
        role="dialog"
        aria-modal="true"
        show={modalOpen}
        enter="transition ease-in-out duration-200"
        enterStart="opacity-0 translate-y-4"
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-in-out duration-200"
        leaveStart="opacity-100 translate-y-0"
        leaveEnd="opacity-0 translate-y-4"
      >
        <div
          ref={modalContent}
          className="bg-white overflow-auto max-w-xl w-full h-[550px] rounded-2xl shadow-lg flex flex-col"
        >
          <div class="p-10">
            <h3 class="mb-6 text-xl font-medium text-gray-900 flex justify-center">
              New Customer
            </h3>

            <form className="w-full space-y-8">
              <InputField
                type="text"
                name="name"
                label="Customer name"
                placeholder="Enter Customer Name"
                wrapperClass="w-full"
                value={formik.values.name}
                onChange={(e) => {
                  const value = sanitizeInput(e.target.value);
                  formik.setFieldValue("name", value);
                }}
                onBlur={formik.handleBlur}
                error={
                  Boolean(formik.touched.name && formik.errors.name)
                    ? formik.errors.name
                    : null
                }
                required
              />
              <div className="space-y-1">
                <label for="country" class="text-sm font-medium">
                  Time Zone
                  <span className="text-rose-500">&nbsp;*</span>
                </label>
                <Select
                  name="time_zone_id"
                  className="unresetText text-left"
                  placeholder="Select Zone"
                  menuPortalTarget={document.body}
                  menuPlacement="top"
                  options={timezoneOptions}
                  isClearable
                  value={formik.values.time_zone_id}
                  onChange={(val) => {
                    formik.setFieldValue("time_zone_id", val);
                  }}
                  onBlur={formik.handleBlur}
                  styles={{
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                  }}
                  classNames={{
                    control: (style) => {
                      return `!border !shadow-sm ${
                        style.isFocused
                          ? "!border-[--bg-color-300]/50"
                          : "!border-slate-200 hover:!border-gray-300"
                      }`;
                    },
                    container: (style) => {
                      return "!h-[37.5px] !font-inter !text-sm";
                    },
                    menuList: (style) => {
                      return `!max-h-[200px]`;
                    },
                  }}
                />
                {Boolean(
                  formik.touched.time_zone_id && formik.errors.time_zone_id
                ) && (
                  <div className="text-xs text-rose-600 pt-1 pl-1">
                    {formik.errors.time_zone_id}
                  </div>
                )}
              </div>
              <div>
                <InputField
                  type="email"
                  name="email"
                  label="Customer Email ID"
                  placeholder="Enter Customer Email ID"
                  tooltiptype="question"
                  tooltip="This email ID will be used to login as a customer"
                  required
                  value={formik.values.email}
                  onChange={(e) => {
                    const value = sanitizeInput(e.target.value);
                    formik.setFieldValue("email", value);
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    Boolean(formik.touched.email && formik.errors.email)
                      ? formik.errors.email
                      : null
                  }
                />
                <FormikProvider value={formik}>
                  <Form autoComplete="off" noValidate>
                    <FieldArray
                      name="additional_email"
                      render={(arrayHelpers) => (
                        <div className="space-y-1">
                          {formik.values.additional_email.length > 0
                            ? formik.values.additional_email.map(
                                (row, index) => {
                                  return (
                                    <div className="flex items-baseline space-x-8 mt-2">
                                      <InputField
                                        type="text"
                                        wrapperClass="w-full"
                                        placeholder="Enter Customer Email ID"
                                        name="additional_email"
                                        value={
                                          formik.values.additional_email[index]
                                        }
                                        onBlur={formik.handleBlur}
                                        onChange={(event) => {
                                          const value = sanitizeInput(
                                            event.target.value
                                          );
                                          formik.setFieldValue(
                                            `additional_email[${index}]`,
                                            value
                                          );
                                        }}
                                        error={
                                          Boolean(
                                            formik.touched &&
                                              formik.touched.additional_email &&
                                              formik.touched.additional_email[
                                                index
                                              ] &&
                                              formik.errors &&
                                              formik.errors.additional_email &&
                                              formik.errors.additional_email[
                                                index
                                              ]
                                          )
                                            ? formik.errors.additional_email[
                                                index
                                              ]
                                            : null
                                        }
                                      />
                                      <span
                                        title="Delete"
                                        className="fa-light fa-trash-can text-rose-500 cursor-pointer"
                                        onClick={(e) =>
                                          arrayHelpers.remove(index)
                                        }
                                      ></span>
                                    </div>
                                  );
                                }
                              )
                            : null}
                          <span
                            className={`text-xs font-semibold leading-3 tracking-tight text-[--bg-color-500] cursor-pointer my-2`}
                            onClick={() => {
                              arrayHelpers.push("");
                            }}
                          >
                            + Add Additional Email ID
                          </span>
                        </div>
                      )}
                    />
                  </Form>
                </FormikProvider>
              </div>
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-slate-800 font-medium text-sm">
                    Activate Tracking for Customer
                  </div>
                </div>
                <div className="flex items-center ml-4">
                  <div className="form-switch">
                    <div>
                      <input
                        type="checkbox"
                        id={"comments1"}
                        name="is_trackable"
                        className="sr-only"
                        checked={formik.values.is_trackable}
                        onChange={(e) => {
                          formik.setFieldValue(
                            "is_trackable",
                            e.target.checked
                          );
                        }}
                      />
                      <label htmlFor={"comments1"} className="bg-slate-400">
                        <span
                          className="bg-white shadow-sm"
                          aria-hidden="true"
                        ></span>
                        <span className="sr-only">{"tooltip"}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div class="flex nowrap mt-10">
                <button
                  className=" w-1/2 btn bg-white border-slate-200 hover:border-slate-300 text-slate-600 mr-2"
                  type="button"
                  onClick={() => {
                    setModalOpen(false);
                    formik.resetForm();
                  }}
                >
                  Cancel
                </button>
                <button
                  className=" w-1/2 btn !bg-[--bg-color-500] text-white hover:!bg-[--bg-color-600] ml-2"
                  onClick={formik.handleSubmit}
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      </CssTransition>
    </>
  );
}
